import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { authApi, AuthResponse } from '../services/api';
import { User } from '../shared';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  handleCallback: (code: string, state: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  // Check if user is authenticated on mount
  const { isLoading } = useQuery(
    'user',
    () => authApi.me().then(res => res.data.data),
    {
      retry: false,
      onSuccess: (data) => {
        if (data) {
          setUser(data);
          localStorage.setItem('user', JSON.stringify(data));
        }
      },
      onError: () => {
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      },
    }
  );

  // Login mutation
  const loginMutation = useMutation(
    () => authApi.login().then(res => res.data.data),
    {
      onSuccess: (data) => {
        if (data?.authUrl) {
          window.location.href = data.authUrl;
        }
      },
    }
  );

  // Callback mutation
  const callbackMutation = useMutation(
    ({ code, state }: { code: string; state: string }) =>
      authApi.callback(code, state).then(res => res.data.data!),
    {
      onSuccess: (data: AuthResponse) => {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        queryClient.invalidateQueries('user');
      },
    }
  );

  // Logout mutation
  const logoutMutation = useMutation(
    () => authApi.logout(),
    {
      onSuccess: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        setUser(null);
        queryClient.clear();
        window.location.href = '/login';
      },
    }
  );

  // Initialize user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('auth_token');
    
    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      }
    }
  }, []);

  const login = async () => {
    await loginMutation.mutateAsync();
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  const handleCallback = async (code: string, state: string) => {
    await callbackMutation.mutateAsync({ code, state });
  };

  const value: AuthContextType = {
    user,
    isLoading: isLoading || loginMutation.isLoading || callbackMutation.isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    handleCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}